import { useState, useRef, useContext, useEffect } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import MyLogs from "./My Logs/MyLogs";
import TeamLogs from "./Team Logs/TeamLogs";
import { Button } from "primereact/button";
import { ROLES } from "../../../../util/roleNameConstants";
import { Menu } from "primereact/menu";
import { Toast } from "primereact/toast";
import { MenuItem } from "primereact/menuitem";
import { SORT_FILTER, TIMELOG_STATUS_FILTER } from "../../../../util/constants";
import { TLFilterContext } from "./TL_FiltersContext";
import { fetchProjects, fetchVehicles } from "./Team Logs/fetchData";
import { useDebounce } from "../../../../util/debounce";
import FilterButton from "../common/FilterButton";
import { showError, showSuccess } from "../../../../util/ShowStatus";
import { getErrorMessage } from "../../../../helper/getResponseMessage";
import { convertEpochtoDate } from "../../../../helper/DateConverters";
import axios from "../../../../util/axiosconfig";
import CalendarDateSelector from "../common/CalendarDateSelector";
import { Dialog } from "primereact/dialog";

const TimeLogManagement = () => {
  const menu = useRef<Menu>(null);
  const toast = useRef<Toast>(null);
  const [debouncedName, setDebouncedName] = useState("");
  const debouncedValue = useDebounce(debouncedName); // Adjust delay as needed

  const context = useContext(TLFilterContext);
  if (!context) {
    throw new Error("FilterPage must be used within a FilterProvider");
  }

  const { filters, setFilters } = context;
  const [vehicleListLoading, setVehicleListLoading] = useState<boolean>(false);
  const [vehiclesList, setVehiclesList] = useState<any>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [projectsList, setProjectsList] = useState<any>([]);
  const [projectListLoading, setProjectListLoading] = useState<boolean>(false);

  const [nameFilter, setNameFilter] = useState<string | null>(null);
  const [sortFilter, setSortFilter] = useState<any>({
    sortField: "registration_no",
    sortOrder: true,
  });
  const [showExportDialog, setShowExportDialog] = useState<boolean>(false);
  const [showExportOptionsDialog, setShowExportOptionsDialog] =
    useState<boolean>(false);
  const [exportSubmitting, setExportSubmitting] = useState<boolean>(false);
  const [isExportDisabled, setIsExportDisabled] = useState<boolean>(false);

  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      name: debouncedValue,
    }));
  }, [debouncedValue, setFilters]);

  useEffect(() => {
    fetchVehicles(setVehiclesList, setVehicleListLoading, toast);
    fetchProjects(setProjectsList, setProjectListLoading, toast);
  }, []);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isWorkerView, setIsWorkerView] = useState(true);

  const clearFilters = () => {
    setFilters((prevFilter: any) => ({
      ...prevFilter,
      vehicleRegistration: null,
      name: null,
      status: null,
      project: null,
    }));
    setNameFilter(null);
  };

  const [timeLogStatusFilter, setTimeLogStatusFilter] = useState<any>(
    TIMELOG_STATUS_FILTER
  );

  useEffect(() => {
    if (activeIndex === 1) {
      setTimeLogStatusFilter([
        // ...TIMELOG_STATUS_FILTER.filter((s) => s.value === "PENDING"),
        ...TIMELOG_STATUS_FILTER.filter((s) => s.value === "SUBMITTED"),
        ...TIMELOG_STATUS_FILTER.filter((s) => s.value === "APPROVED"),
        ...TIMELOG_STATUS_FILTER.filter((s) => s.value === "REJECTED"),
        ...TIMELOG_STATUS_FILTER.filter(
          (s) => s.value === "REJECTED BY MANAGER"
        ),
        ...TIMELOG_STATUS_FILTER.filter(
          (s) => s.value === "SUBMITTED TO MANAGER"
        ),
        ...TIMELOG_STATUS_FILTER.filter(
          (s) => s.value === "APPROVED BY MANAGER"
        ),
      ]);
    } else {
      setTimeLogStatusFilter(TIMELOG_STATUS_FILTER);
    }
  }, [activeIndex]);

  const projectOptionLabel = (option: any) => {
    if (!option) {
      return <span>Select a Project</span>;
    }

    return `${option?.projectKey} - ${option?.name}`;
  };

  const items = [
    {
      items: [
        {
          template: (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-3">
              <label htmlFor="vehickeRegistration" className="tw-font-semibold">
                Vehicle Registration
              </label>
              <Dropdown
                className="tw-h-11 tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-w-full placeholder:tw-font-normal filter-dropdown"
                value={filters?.vehicleRegistration ?? null}
                // options={vehicleTypes}
                options={vehiclesList}
                optionLabel="registration_no" // Assuming 'roles' contains an array of objects with a 'role' key
                placeholder="Select Vehicle Registration"
                onChange={(e) => {
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    vehicleRegistration: e.target.value,
                  }));
                }}
                loading={vehicleListLoading}
                filter
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ),
        },
        {
          template: (
            <>
              {activeIndex === 1 && (
                <div>
                  <label htmlFor="name" className="tw-ml-5 tw-font-semibold">
                    Name
                  </label>
                  <div className="tw-flex tw-gap-2 tw-items-center tw-mx-5 tw-w-62 tw-mt-2">
                    <InputText
                      type="text"
                      className="tw-pl-2 tw-border tw-border-gray-300 tw-rounded-md tw-w-full tw-h-11"
                      value={nameFilter ?? ""}
                      placeholder="Enter Name"
                      onChange={(e) => {
                        e.stopPropagation();
                        setNameFilter(e.target.value);
                        setDebouncedName(e.target.value);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}
            </>
          ),
        },
        {
          template: (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-3">
              <label htmlFor="vehickeRegistration" className="tw-font-semibold">
                Project
              </label>
              <Dropdown
                className="tw-h-11 tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-w-full placeholder:tw-font-normal filter-dropdown"
                value={filters?.project ?? null}
                // options={vehicleTypes}
                options={projectsList}
                // optionLabel={}
                itemTemplate={projectOptionLabel}
                valueTemplate={projectOptionLabel}
                placeholder="Select a Project"
                onChange={(e) => {
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    project: e.target.value,
                  }));
                }}
                loading={projectListLoading}
                filter
                filterBy="projectKey,name"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ),
        },
        {
          template: (
            <div className="tw-flex tw-flex-wrap tw-gap-[20px] tw-justify-start tw-items-center tw-ml-[21px] tw-pt-[12px]">
              {timeLogStatusFilter?.map((status: any, index: number) => {
                if (activeIndex === 1 && status.value === "PENDING") {
                  return <div key={index}>{"  "}</div>;
                }
                return (
                  <div
                    key={status.id}
                    className="tw-flex tw-items-center"
                    onClick={() => {
                      setFilters((prevFilters) => ({
                        ...prevFilters,
                        status: status.value,
                      }));
                    }}
                  >
                    <span
                      className={`a-badge tw-border tw-cursor-pointer ${
                        filters.status === status.value
                          ? "selected-filter-status"
                          : ""
                      }`}
                    >
                      <span
                        className={`${status.dotColour} tw-w-2 tw-h-2 tw-rounded-full`}
                      ></span>
                      <span className="badge-value tw-text-ellipsis tw-overflow-hidden tw-max-w-52">
                        {status.label}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          ),
        },
        {
          template: (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-4">
              <Button
                className="tw-flex tw-px-3 tw-py-2 tw-rounded tw-bg-red-500 hover:tw-bg-red-500 tw-font-medium tw-gap-2 tw-text-white"
                onClick={clearFilters} // This function will reset filters
              >
                Clear Filters
              </Button>
            </div>
          ),
        },
      ],
    },
  ];

  const handleExportTimelog = async (type?: string) => {
    setIsExporting(true);
    try {
      setExportSubmitting(true);
      // Prepare the query parameters from filters
      const params: any = {
        sortField: `${isWorkerView ? "displayName" : "registration_no"} , ${
          sortFilter.sortField
        }`,
        sortOrder:
          sortFilter.sortOrder === true ? SORT_FILTER.ASC : SORT_FILTER.DESC,
      };

      if (filters) {
        params.startDate = filters.startDate;
        params.endDate = filters.endDate;
        params.vehicleID = filters?.vehicleRegistration?.vehicleID;
        filters.name !== "" && (params.displayName = filters.name);
        filters.status && (params.status = filters.status);
      }
      // const response = await axios.get("tl-user/team-logs-list", { params });

      // Add view type
      // params.append('isWorkerView', isWorkerView.toString());

      if (type === "pdf") {
        const response: any = await axios.post(
          `tl-user/export-teamlogs-pdf`,
          params
        );
        // Check if there are results to export
        if (response.data.result && response.data.result.length === 0) {
          showError(response.data.message, toast);
          return;
        }

        // Extract file URL and name from response
        const { fileUrl, fileName } = response.data;

        // Create download link using the file URL from response
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.remove();
        window.URL.revokeObjectURL(fileUrl);
      } else {
        const response = await axios.post(`tl-user/export-time-sheet`, params);
        // Handle the response (assuming it's a blob for file download)
        // const url = window.URL.createObjectURL(new Blob([response.data]));
        const url = response.data.path;
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `timelog_report_${
            filters.startDate ? convertEpochtoDate(filters.startDate) : ""
          }_${filters.endDate ? convertEpochtoDate(filters.endDate) : ""}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
      }

      showSuccess(`Timelog exported successfully.`, toast);
    } catch (err: unknown) {
      const error = getErrorMessage(err);
      showError(error, toast);
    } finally {
      setIsExporting(false);
      setExportSubmitting(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <div className="tw-flex tw-gap-4 tw-justify-between tw-items-center tw-w-full tw-p-4 tw-bg-[#F8FAFC]">
        <Tooltip target=".description-tooltip-target" />
        <div className="tw-flex tw-justify-start tw-items-center">
          <Button
            type="button"
            onClick={() => setActiveIndex(0)}
            className={`tw-bg-transparent ${
              activeIndex === 0
                ? "tw-border-0 tw-border-b-2 tw-border-b-solid tw-border-b-[#00AFEE] tw-rounded-none"
                : "tw-text-[#9da39e]"
            }   focus:tw-shadow-none tw-px-4 tw-py-1 tw-text-xl tw-font-semibold`}
          >
            My Logs
          </Button>
          {(JSON.parse(localStorage.userAssociated) === true ||
            localStorage.roleName.includes(ROLES.TIMELOG_ADMIN) ||
            localStorage.roleName.includes(ROLES.TIMELOG_HR)) && (
            <Button
              type="button"
              onClick={() => setActiveIndex(1)}
              className={`tw-bg-transparent ${
                activeIndex === 1
                  ? "tw-border-0 tw-border-b-2 tw-border-b-solid tw-border-b-[#00AFEE] tw-rounded-none"
                  : "tw-text-[#9da39e]"
              }  focus:tw-shadow-none tw-px-4 tw-py-1 tw-text-xl tw-font-semibold`}
            >
              Team Logs{" "}
            </Button>
          )}
        </div>
        <div className="tw-flex tw-mb-2 tw-gap-2 tw-justify-content-end tw-items-center">
          {activeIndex === 1 &&
            (localStorage.roleName.includes(ROLES.TIMELOG_ADMIN) ||
              localStorage.roleName.includes(ROLES.TIMELOG_HR)) && (
              <div className="tw-flex tw-items-center">
                <label
                  htmlFor="vehicleView"
                  className="tw-text-sm tw-text-[#64748B] tw-mr-2"
                >
                  Vehicle View
                </label>
                <InputSwitch
                  id="vehicleView"
                  checked={isWorkerView}
                  onChange={(e) => setIsWorkerView(e.value)}
                />
                <label
                  htmlFor="vehicleView"
                  className="tw-text-sm tw-text-[#64748B] tw-ml-2"
                >
                  Worker View
                </label>
              </div>
            )}
          <CalendarDateSelector setDateRange={setFilters} />

          <FilterButton menu={menu} />

          <Menu
            // className={`${activeIndex === 0 ? "tw-w-[360px]" : "tw-w-[360px]"}`}
            className="tw-w-[360px]"
            model={items as MenuItem[]}
            popup
            ref={menu}
          />
          <>
            {activeIndex === 1 && (
              <Button
                type="button"
                icon="pi pi-upload"
                label="Export"
                // loading={isExporting}
                // disabled={isExportDisabled}
                onClick={() => setShowExportOptionsDialog(true)}
                className="tw-flex tw-gap-[10px] focus:tw-shadow-none hover:tw-bg-[#3f3c3c] tw-items-center tw-justify-start tw-bg-[#555555] tw-border-[#555555] tw-text-white tw-py-2 tw-px-4 tw-rounded tw-h-full"
              />
            )}
          </>
        </div>
      </div>

      <TabView
        className="time-log-tab tw-w-full"
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        <TabPanel header="My Log">
          <div>
            <MyLogs />
          </div>
        </TabPanel>
        <TabPanel header="Time Log">
          <div className="card">
            <TeamLogs
              isWorkerView={isWorkerView}
              vehiclesList={vehiclesList}
              vehicleListLoading={vehicleListLoading}
              sortFilter={sortFilter}
              setSortFilter={setSortFilter}
              setIsExportDisabled={setIsExportDisabled}
            />
          </div>
        </TabPanel>
      </TabView>

      {/* Export Options Dialog */}
      <Dialog
        visible={showExportOptionsDialog}
        onHide={() => !exportSubmitting && setShowExportOptionsDialog(false)}
        header="Export Options"
        style={{ width: "400px" }}
        closable={!exportSubmitting}
        draggable={false}
      >
        <div className="tw-p-4">
          <p className="tw-mb-6 tw-text-gray-700">
            Please select your export preference:
          </p>
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Button
              label="Export PDFs"
              icon="pi pi-image"
              onClick={() => handleExportTimelog("pdf")}
              disabled={exportSubmitting}
              loading={exportSubmitting}
              className="tw-w-full tw-p-3 tw-bg-primary tw-border-primary tw-text-white tw-font-medium focus:tw-shadow-none"
            />
            <Button
              label="Export Excel Sheet"
              icon="pi pi-file-text"
              onClick={() => handleExportTimelog("excel")}
              disabled={exportSubmitting}
              className="tw-w-full tw-p-3 tw-bg-gray-600 tw-border-gray-600 tw-text-white tw-font-medium focus:tw-shadow-none"
            />
          </div>
          {!exportSubmitting && (
            <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-200">
              <Button
                label="Cancel"
                onClick={() => setShowExportOptionsDialog(false)}
                className="tw-w-full tw-p-2 tw-bg-white tw-border tw-border-gray-300 tw-text-gray-700 tw-font-medium focus:tw-shadow-none hover:tw-bg-gray-50"
              />
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default TimeLogManagement;
